<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('contractor.contractors') }}
        </span>
        <h3 class="page-title">
          {{ $t('contractor.editContractor') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/wykonawcy')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('contractor.doEditContractor') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="contractor-edit-form" tag="form">
        <d-card class="mb-5">
          <d-tabs card v-if="!loadError">
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <div class="form-group">
                <label>
                  {{ $t('ui.name') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />

                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.emailAddress') }}:
                </label>

                <validation-provider
                  rules="required|email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="email"
                    v-model="email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-tab>

            <d-tab :title="$t('price.pricesLists')">
              <d-row>
                <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
                  <h4 class="mb-3 pb-3">
                    {{ $t('price.pricesLists') }}
                  </h4>
                </d-col>
                <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-start">
                  <d-button
                    @click.prevent="openImportPopup"
                    theme="primary"
                    class="mr-2">
                    <i class="material-icons">
                      cloud_upload
                    </i>
                    {{ $t('price.importPrices') }}
                  </d-button>

                  <d-button
                    @click.prevent="openExportPopup"
                    theme="secondary"
                    class="mr-2">
                    <i class="material-icons">
                      cloud_download
                    </i>
                    {{ $t('price.exportPrices') }}
                  </d-button>

                  <d-button
                    theme="success"
                    @click.native.prevent="openPricePopup()">
                    <i class="material-icons">add</i>
                    {{ $t('price.addNewPrice') }}
                  </d-button>
                </d-col>
              </d-row>

              <contractor-prices
                :contractor-id="contractorID" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <contractor-prices-export-popup
      v-if="showPricesExportPopup"
      :currencies="currencies"
      :contractor-id="contractorID" />

    <contractor-prices-import-popup
      v-if="showPricesImportPopup"
      :currencies="currencies"
      :contractor-id="contractorID" />

    <contractor-price-popup
      v-if="showPricePopup"
      :price-data="selectedPrice"
      :currencies="currencies"
      :services="services"
      :contractor-id="contractorID" />
  </d-container>
</template>

<script>
import ContractorPrices from './ContractorPrices.vue';
import ContractorPricePopup from './ContractorPricePopup.vue';
import ContractorPricesExportPopup from './ContractorPricesExportPopup.vue';
import ContractorPricesImportPopup from './ContractorPricesImportPopup.vue';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'contractor-edit',
  components: {
    'contractor-prices': ContractorPrices,
    'contractor-price-popup': ContractorPricePopup,
    'contractor-prices-export-popup': ContractorPricesExportPopup,
    'contractor-prices-import-popup': ContractorPricesImportPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedCurrencies && this.loadedServices;
    },
    contractorID () {
      return parseInt(this.$route.params.id, 10);
    }
  },
  data() {
    return {
      name: '',
      email: '',
      currencies: [],
      services: [],
      selectedPrice: 0,
      loadedData: false,
      loadedCurrencies: false,
      loadedServices: false,
      loadError: false,
      showPricePopup: false,
      showPricesExportPopup: false,
      showPricesImportPopup: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: '/contractors/item/',
      instanceFields: ['name', 'email'],
      loadedKey: 'loadedData',
      errorKey: 'loadError'
    });

    this.loadAdditionalData();
    this.$bus.$on('contractor-prices-export-popup-close', this.closeExportPopup);
    this.$bus.$on('contractor-prices-import-popup-close', this.closeImportPopup);
    this.$bus.$on('contractor-price-popup-open', this.openPricePopup);
    this.$bus.$on('contractor-price-popup-close', this.closePricePopup);
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError',
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/all-items',
        loadedKey: 'loadedServices',
        outputKey: 'services',
        errorKey: 'loadError'
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['contractor-edit-form'], this.submitForm);
    },
    openPricePopup (priceData = {}) {
      this.showPricePopup = true;
      this.selectedPrice = priceData;
    },
    openExportPopup () {
      this.showPricesExportPopup = true;
    },
    openImportPopup () {
      this.showPricesImportPopup = true;
    },
    closePricePopup () {
      this.showPricePopup = false;
    },
    closeExportPopup () {
      this.showPricesExportPopup = false;
    },
    closeImportPopup () {
      this.showPricesImportPopup = false;
    },
    submitForm () {
      let formDataToSend = {
        name: this.name,
        email: this.email
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/contractors/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('contractor.editContractorSuccessTitle'),
        successText: this.$t('contractor.editContractorSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('contractor.editContractorErrorText')
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('contractor-prices-export-popup-close', this.closeExportPopup);
    this.$bus.$off('contractor-prices-import-popup-close', this.closeImportPopup);
    this.$bus.$off('contractor-price-popup-open', this.openPricePopup);
    this.$bus.$off('contractor-price-popup-close', this.closePricePopup);
  }
};
</script>
